import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useContentToken,
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useComfyCtaStyles } from '../hooks/useComfyCtaStyles';

// ============================================================================
// Slot groups
// ============================================================================

const imageDef: SlotGroupDefinition = {
  label: 'Pet Policy Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      // Calm outdoor dog portrait — closed/minimal tongue (replaces the
      // prior Labrador with a large tongue lick).
      defaultValue:
        'https://images.unsplash.com/photo-1587300003388-59208cc962cb?auto=format&fit=crop&w=1200&q=80',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Friendly dog outdoors with a calm, happy expression',
    },
  },
  // Wire the media picker to photo_gallery so choosing a new photo also
  // updates the alt text — otherwise the picker only swaps the image and
  // the alt/caption stay on the previous value.
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Floor Plans CTA',
  fields: {
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Explore Pet Friendly Apartments',
    },
    url: { label: 'Button URL', type: 'url', defaultValue: '/floor-plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Helpers — currency chrome for fee/rent stats. Skips empty placeholders
// and avoids doubling when the property value already includes a leading "$".
// ============================================================================

function hasDollarPrefix(value: string | null | undefined): boolean {
  return (value ?? '').trim().startsWith('$');
}

function isEmptyStat(value: string | null | undefined): boolean {
  const trimmed = (value ?? '').trim();
  return !trimmed || trimmed === '—';
}

// ============================================================================
// Component — a single dark callout card with copy on the left and a photo
// on the right. Composition: eyebrow → mixed-emphasis heading → body →
// floor-plans CTA → four pet policy stats. Adapted from Belmont
// PetPolicyCallout for this theme's PetPolicy slot + comfy-cta family.
// ============================================================================

export function PetPolicy() {
  const image = useSlotGroup('petpolicy.image', imageDef);
  const cta = useSlotGroup('petpolicy.cta_link', ctaLinkDef);
  const eyebrow = useContentToken('petpolicy.eyebrow');
  const stat1Value = useContentToken('petpolicy.stat_1_value');
  const stat1Label = useContentToken('petpolicy.stat_1_label');
  const stat2Value = useContentToken('petpolicy.stat_2_value');
  const stat2Label = useContentToken('petpolicy.stat_2_label');
  const stat3Value = useContentToken('petpolicy.stat_3_value');
  const stat3Label = useContentToken('petpolicy.stat_3_label');
  const stat4Value = useContentToken('petpolicy.stat_4_value');
  const stat4Label = useContentToken('petpolicy.stat_4_label');

  const showDollarFee = !isEmptyStat(stat2Value) && !hasDollarPrefix(stat2Value);
  const showDollarRent = !isEmptyStat(stat3Value) && !hasDollarPrefix(stat3Value);
  const showDollarSecondRent = !isEmptyStat(stat4Value) && !hasDollarPrefix(stat4Value);

  useRevealStyles();
  useComfyCtaStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={sectionRef}
      className="reveal-fade-up pp-section"
    >
      <div className="pp-inner">
        <div className="pp-card">
          <div className="pp-copy">
            <EditableToken
              path="petpolicy.eyebrow"
              as="p"
              className="pp-eyebrow"
            >
              {eyebrow || 'Pet-Friendly Living'}
            </EditableToken>
            <RichTextContent
              path="petpolicy.heading"
              label="Heading"
              as="h2"
              className="pp-heading"
            />
            <RichTextContent
              path="petpolicy.description"
              label="Description"
              as="div"
              className="pp-body"
            />

            {/* CTA between copy and stats — filled surface button linking
                to floor plans (same beat as the reference composition). */}
            <div className="pp-cta">
              <EditableSlotGroup
                groupPath="petpolicy.cta_link"
                definition={ctaLinkDef}
              >
                <a
                  href={cta.url || '/floor-plans'}
                  target={cta._target || '_self'}
                  className="comfy-cta pp-cta-link"
                  style={{
                    // Surface fill on the primary panel (Belmont callout).
                    // Resting label is white; hover flips to primary ink on
                    // an on-primary chip.
                    ['--comfy-cta-bg' as string]:
                      'var(--vibe-color-surface, #f5f5f5)',
                    ['--comfy-cta-fg' as string]: '#ffffff',
                    ['--comfy-cta-bg-hover' as string]:
                      'var(--vibe-color-text-on-primary, #ffffff)',
                    ['--comfy-cta-fg-hover' as string]:
                      'var(--vibe-color-primary, #101a3a)',
                  }}
                >
                  {cta.label || 'Explore Pet Friendly Apartments'}
                  <span className="pp-cta-arrow" aria-hidden="true">
                    →
                  </span>
                </a>
              </EditableSlotGroup>
            </div>

            <div className="pp-stats" aria-label="Pet policy details">
              <div className="pp-stats-grid">
                <div className="pp-stat">
                  <EditableToken
                    path="petpolicy.stat_1_value"
                    as="p"
                    className="pp-stat-value"
                  >
                    {stat1Value}
                  </EditableToken>
                  <EditableToken
                    path="petpolicy.stat_1_label"
                    as="p"
                    className="pp-stat-label"
                  >
                    {stat1Label}
                  </EditableToken>
                </div>
                <div className="pp-stat">
                  <p className="pp-stat-value">
                    {showDollarFee ? <span>$</span> : null}
                    <EditableToken path="petpolicy.stat_2_value" as="span">
                      {stat2Value}
                    </EditableToken>
                  </p>
                  <EditableToken
                    path="petpolicy.stat_2_label"
                    as="p"
                    className="pp-stat-label"
                  >
                    {stat2Label}
                  </EditableToken>
                </div>
                <div className="pp-stat">
                  <p className="pp-stat-value">
                    {showDollarRent ? <span>$</span> : null}
                    <EditableToken path="petpolicy.stat_3_value" as="span">
                      {stat3Value}
                    </EditableToken>
                  </p>
                  <EditableToken
                    path="petpolicy.stat_3_label"
                    as="p"
                    className="pp-stat-label"
                  >
                    {stat3Label}
                  </EditableToken>
                </div>
                <div className="pp-stat">
                  <p className="pp-stat-value">
                    {showDollarSecondRent ? <span>$</span> : null}
                    <EditableToken path="petpolicy.stat_4_value" as="span">
                      {stat4Value}
                    </EditableToken>
                  </p>
                  <EditableToken
                    path="petpolicy.stat_4_label"
                    as="p"
                    className="pp-stat-label"
                  >
                    {stat4Label}
                  </EditableToken>
                </div>
              </div>
            </div>
          </div>
          <EditableSlotGroup
            groupPath="petpolicy.image"
            definition={imageDef}
            className="pp-media-slot"
          >
            <div className="pp-media">
              <VibesitesImage
                url={image.image || ''}
                alt={image.alt || 'Pet policy'}
                className="pp-media-img"
                sizes="(min-width: 860px) 540px, 90vw"
              />
            </div>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .pp-section {
          background-color: #F6F7F7;
          padding: 5rem 1.25rem;
          /* Contain any long CTA/copy so the page never scrolls sideways. */
          overflow-x: clip;
        }
        @media (max-width: 640px) {
          .pp-section {
            padding: 3.25rem 1rem;
          }
        }

        .pp-inner {
          max-width: 1180px;
          margin: 0 auto;
          min-width: 0;
          width: 100%;
        }

        /* Two-panel card: copy on the left, image on the right.
           Mobile stacks image above copy via order (desktop unchanged). */
        .pp-card {
          display: grid;
          grid-template-columns: minmax(0, 1fr);
          overflow: hidden;
          border-radius: 4px;
          min-width: 0;
          width: 100%;
        }
        @media (min-width: 860px) {
          .pp-card {
            grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
            min-height: 480px;
          }
        }

        /* Mobile: photo first, then copy. Desktop keeps DOM order
           (copy | image). */
        @media (max-width: 859px) {
          .pp-media-slot {
            order: 1;
            display: block;
            min-width: 0;
            max-width: 100%;
          }
          .pp-copy {
            order: 2;
          }
        }

        /* Brand primary panel — text uses text-on-primary so contrast stays
           AA when the brand primary color changes. */
        .pp-copy {
          background-color: var(--vibe-color-primary, #101a3a);
          color: var(--vibe-color-text-on-primary, #ffffff);
          padding: clamp(1.75rem, 5vw, 4rem);
          display: flex;
          flex-direction: column;
          justify-content: center;
          gap: 0;
          min-width: 0;
          max-width: 100%;
          box-sizing: border-box;
        }
        @media (max-width: 640px) {
          .pp-copy {
            padding: 1.75rem 1.25rem 2rem;
          }
        }

        /* Eyebrow with a short accent rule — matches the reference
           "PET-FRIENDLY LIVING" treatment. Surface is the warm accent
           on the primary panel. */
        .pp-eyebrow {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin: 0 0 1rem;
          font-family: var(--vibe-font-body);
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          color: var(--vibe-color-surface, #f5f5f5);
          max-width: 100%;
        }
        .pp-eyebrow::before {
          content: '';
          display: block;
          width: 1.5rem;
          height: 1.5px;
          background: currentColor;
          flex-shrink: 0;
        }

        .pp-heading {
          margin: 0 0 1.25rem;
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.6rem, 3.6vw, 2.75rem);
          line-height: 1.12;
          font-weight: 400;
          color: var(--vibe-color-text-on-primary, #ffffff);
          overflow-wrap: break-word;
          word-wrap: break-word;
        }
        .pp-heading [data-vibesite-richtext] p {
          margin: 0;
        }
        /* Accent phrase — italic + surface color so "here, too." pops
           against the on-primary heading, like the reference. */
        .pp-heading em,
        .pp-heading [data-vibesite-richtext] em {
          font-style: italic;
          font-weight: 400;
          color: var(--vibe-color-surface, #f5f5f5);
        }

        /* Match site body copy (AmenitiesList / PropertyOverview:
           text-base sm:text-lg leading-relaxed ≈ 1.7). */
        .pp-body {
          margin: 0;
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.7;
          color: var(--vibe-color-text-on-primary, #ffffff);
          max-width: 100%;
          overflow-wrap: break-word;
          word-wrap: break-word;
        }
        @media (min-width: 640px) {
          .pp-body {
            font-size: 1.125rem;
          }
        }
        /* Re-declare type on <p> so editor resets can't crush line-height. */
        .pp-body [data-vibesite-richtext] p {
          margin: 0 0 1rem;
          font-family: inherit;
          font-size: inherit;
          font-weight: inherit;
          line-height: 1.7;
        }
        .pp-body [data-vibesite-richtext] p:last-child {
          margin-bottom: 0;
        }
        .pp-body [data-vibesite-richtext] a {
          color: var(--vibe-color-text-on-primary, #ffffff);
          text-decoration: underline;
          overflow-wrap: anywhere;
        }

        .pp-cta {
          margin-top: clamp(1.5rem, 3vw, 2rem);
          min-width: 0;
          max-width: 100%;
        }
        /* EditableSlotGroup wrapper must not force the card wider than
           the viewport when the CTA label is long. */
        .pp-cta > * {
          display: block;
          max-width: 100%;
          min-width: 0;
        }

        /* CTA — desktop/tablet share natural comfy-cta sizing. Surface fill
           with white resting label (hover swaps to primary ink). */
        a.comfy-cta.pp-cta-link {
          box-sizing: border-box;
          width: auto;
          max-width: 100%;
          min-width: 0;
          white-space: nowrap;
          line-height: 1;
          padding: 1rem 2rem;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          font-size: 0.72rem;
          font-weight: 600;
          gap: 0.35rem;
          justify-content: flex-start;
          text-align: left;
          background: var(--comfy-cta-bg, var(--vibe-color-surface, #f5f5f5));
          color: var(--comfy-cta-fg, #ffffff);
        }
        a.comfy-cta.pp-cta-link:hover,
        a.comfy-cta.pp-cta-link:focus-visible {
          background: var(--comfy-cta-bg-hover, var(--vibe-color-text-on-primary, #ffffff));
          color: var(--comfy-cta-fg-hover, var(--vibe-color-primary, #101a3a));
        }

        /* Mobile only — compact, left-aligned (tablet keeps desktop size). */
        @media (max-width: 640px) {
          a.comfy-cta.pp-cta-link {
            font-size: clamp(0.55rem, 2.4vw, 0.68rem);
            letter-spacing: 0.08em;
            padding: 0.7rem 1.1rem;
          }
        }

        .pp-cta-arrow {
          display: inline-block;
          margin-left: 0.15rem;
          font-size: 1.05em;
          line-height: 1;
          transition: transform 0.2s ease;
        }
        a.comfy-cta.pp-cta-link:hover .pp-cta-arrow,
        a.comfy-cta.pp-cta-link:focus-visible .pp-cta-arrow {
          transform: translateX(3px);
        }
        @media (prefers-reduced-motion: reduce) {
          a.comfy-cta.pp-cta-link:hover .pp-cta-arrow,
          a.comfy-cta.pp-cta-link:focus-visible .pp-cta-arrow {
            transform: none;
          }
        }

        /* Stats band: hairline rule + four large values (2×2 so amounts
           stay readable in the half-width copy panel). */
        .pp-stats {
          margin-top: clamp(1.75rem, 3.5vw, 2.5rem);
          padding-top: clamp(1.5rem, 3vw, 2rem);
          border-top: 1px solid color-mix(in srgb, var(--vibe-color-text-on-primary, #ffffff) 28%, transparent);
          min-width: 0;
          max-width: 100%;
        }

        .pp-stats-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 1.25rem 1rem;
        }
        @media (min-width: 480px) {
          .pp-stats-grid {
            gap: 1.25rem 1.75rem;
          }
        }

        .pp-stat {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }

        /* Mobile: center the stats column(s). Desktop stays left-aligned. */
        @media (max-width: 859px) {
          .pp-stat {
            align-items: center;
            text-align: center;
          }
          .pp-stat-value {
            justify-content: center;
          }
        }

        .pp-stat-value {
          margin: 0;
          display: flex;
          align-items: flex-end;
          font-family: var(--vibe-font-heading);
          font-size: clamp(2rem, 4.2vw, 2.85rem);
          line-height: 1;
          font-weight: 400;
          letter-spacing: -0.02em;
          color: var(--vibe-color-text-on-primary, #ffffff);
        }

        .pp-stat-label {
          margin: 0;
          font-family: var(--vibe-font-body);
          font-size: 0.68rem;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-primary, #ffffff);
          opacity: 0.88;
          overflow-wrap: break-word;
        }

        /* Image panel: matches the copy panel's height so the two sides read
           as one balanced card. object-fit: cover keeps the pet centered even
           if the source aspect ratio doesn't match the panel exactly. */
        .pp-media {
          position: relative;
          min-height: 280px;
          background: var(--vibe-color-surface, #d9d5cc);
          min-width: 0;
        }
        @media (min-width: 640px) {
          .pp-media {
            min-height: 360px;
          }
        }
        @media (min-width: 860px) {
          .pp-media {
            min-height: 100%;
          }
        }
        .pp-media-img {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
      `}</style>
    </section>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'PetPolicy',
  displayName: 'Pet Policy',
  tokens: {
    'petpolicy.eyebrow': {
      label: 'Eyebrow',
      type: 'text',
      defaultValue: 'Pet-Friendly Living',
    },
    'petpolicy.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Your pet belongs <em>here, too.</em></p>',
    },
    'petpolicy.description': {
      label: 'Description',
      type: 'richtext',
      suggestedFields: [
        'property_pet_policy_description',
        'property_petpolicy_info',
        'property_pets_blurb',
      ],
      defaultValue:
        '<p>Home just wouldn&#39;t be the same without your pets, and we invite you to bring them with you to <span data-type="field-token" data-field="property_name"></span>. Questions? Contact the leasing office, or <a href="/pet-policy">review our full pet policy</a>.</p>',
    },
    'petpolicy.stat_1_value': {
      label: 'Stat 1 Value',
      type: 'text',
      defaultField: 'property_max_pets',
      defaultValue: '—',
    },
    'petpolicy.stat_1_label': {
      label: 'Stat 1 Label',
      type: 'text',
      defaultValue: 'Pets per unit',
    },
    'petpolicy.stat_2_value': {
      label: 'Stat 2 Value',
      type: 'text',
      defaultField: 'property_upfront_pet_fee',
      defaultValue: '—',
    },
    'petpolicy.stat_2_label': {
      label: 'Stat 2 Label',
      type: 'text',
      defaultValue: 'Pet fee',
    },
    'petpolicy.stat_3_value': {
      label: 'Stat 3 Value',
      type: 'text',
      defaultField: 'property_monthly_pet_rent',
      defaultValue: '—',
    },
    'petpolicy.stat_3_label': {
      label: 'Stat 3 Label',
      type: 'text',
      defaultValue: 'Pet rent / mo',
    },
    'petpolicy.stat_4_value': {
      label: 'Stat 4 Value',
      type: 'text',
      defaultValue: '25',
    },
    'petpolicy.stat_4_label': {
      label: 'Stat 4 Label',
      type: 'text',
      defaultValue: 'Second pet rent / mo',
    },
  },
  slotGroups: {
    'petpolicy.image': imageDef,
    'petpolicy.cta_link': ctaLinkDef,
  },
};

(PetPolicy as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PetPolicy;
